import { uiText } from '../utils/i18n';
import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useStore } from '../store';
import { useT } from '../utils/i18n';
import { getSkillDisplayName, getSkillIcon, getSkillCover, getSkillCategoryLabel, getSkillAccentColor } from '../data/skill-meta';
import { Avatar } from './shared/Avatar';
import { getAvatar } from '../utils/avatar-store';
import styles from './SkillMarketPage.module.css';

interface HubSkill {
  id: string;
  name: string;
  description: string;
  version: number;
  author: string;
  workspaceId?: string;
  visibility: string;
  tags: string[];
  publishedAt: string;
  updatedAt: string;
}

interface SkillDetail {
  id: string;
  name: string;
  description: string;
  author: string;
  tags: string[];
  skillMd: string;
  userInvocable: boolean;
  context?: string;
  allowedTools?: string;
}

type Category = 'all' | 'system' | 'design' | 'thinking' | 'business';

const SKILL_CATEGORIES: Record<string, Category> = {
  nuwa: 'system',
  'web-design-engineer': 'design',
  'web-video-presentation': 'design',
  'gpt-image-2': 'design',
  'steve-jobs-perspective': 'thinking',
  'munger-perspective': 'thinking',
  'paul-graham-perspective': 'thinking',
  'elon-musk-perspective': 'thinking',
  'feynman-perspective': 'thinking',
  consultant: 'business',
  'contract-review': 'business',
  'grc-compliance': 'business',
};

function getCategoryLabel(cat: Category): string {
  const labels: Record<Category, string> = {
    all: '全部',
    system: '系统工具',
    design: '设计创作',
    thinking: '思维框架',
    business: '商业专业',
  };
  return uiText(labels[cat]);
}

function stripFrontmatter(content: string | undefined): string {
  if (!content) return '';
  return content.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, '');
}

const PERSONA_CHIPS = [
  { emoji: '🧬', label: '女娲 · Skill 造人术', skill: 'nuwa' },
  { emoji: '🍎', label: '乔布斯思维', skill: 'steve-jobs-perspective' },
  { emoji: '🪜', label: '芒格思维', skill: 'munger-perspective' },
  { emoji: '💰', label: 'PG 思维', skill: 'paul-graham-perspective' },
  { emoji: '🚀', label: '马斯克思维', skill: 'elon-musk-perspective' },
  { emoji: '⚛︎', label: '费曼思维', skill: 'feynman-perspective' },
];
const DESIGN_CHIPS = [
  { emoji: '🎨', label: 'Web 设计工程师', skill: 'web-design-engineer' },
  { emoji: '🎬', label: '演示文稿制作', skill: 'web-video-presentation' },
  { emoji: '🖼', label: 'AI 图像生成', skill: 'gpt-image-2' },
];

export function SkillMarketPage() {
  const [skills, setSkills] = useState<HubSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<Category>('all');
  const [search, setSearch] = useState('');
  const [selectedSkill, setSelectedSkill] = useState<SkillDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [installing, setInstalling] = useState<string | null>(null);
  const [installTarget, setInstallTarget] = useState<string | null>(null);
  const [installSuccess, setInstallSuccess] = useState<string | null>(null);

  const bots = useStore((s) => s.bots);
  const t = useT();
  const token = localStorage.getItem('novax:token') || '';

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/skills', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSkills(data.skills || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const detailOpenRef = useRef(false);
  const skillDetailCache = useRef(new Map<string, any>());
  const fetchDetail = async (name: string) => {
    detailOpenRef.current = true;
    const cached = skillDetailCache.current.get(name);
    if (cached) {
      setSelectedSkill(cached);
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setSelectedSkill({ id: '', name, description: '', author: '', tags: [], skillMd: '', userInvocable: false });
    try {
      const res = await fetch(`/api/skills/${encodeURIComponent(name)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && detailOpenRef.current) {
        const data = await res.json();
        setSelectedSkill(data);
        skillDetailCache.current.set(name, data);
      }
    } catch { /* ignore */ }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    detailOpenRef.current = false;
    setSelectedSkill(null);
    setInstallTarget(null);
    setInstallSuccess(null);
  };

  const handleInstall = async (skillName: string, botName: string) => {
    setInstalling(botName);
    try {
      await fetch(`/api/skills/${encodeURIComponent(skillName)}/install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ botName }),
      });
      setInstallSuccess(botName);
      setTimeout(() => {
        setInstallSuccess(null);
        setInstallTarget(null);
      }, 1500);
    } catch { /* ignore */ }
    setInstalling(null);
  };

  const [bannerIdx, setBannerIdx] = useState(0);
  const bannerTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const SLIDE_COUNT = 2;

  useEffect(() => {
    bannerTimer.current = setInterval(() => {
      setBannerIdx((i) => (i + 1) % SLIDE_COUNT);
    }, 5000);
    return () => { if (bannerTimer.current !== null) clearInterval(bannerTimer.current); };
  }, []);

  const goToSlide = (idx: number) => {
    setBannerIdx(idx);
    if (bannerTimer.current !== null) clearInterval(bannerTimer.current);
    bannerTimer.current = setInterval(() => {
      setBannerIdx((i) => (i + 1) % SLIDE_COUNT);
    }, 5000);
  };

  const categories: Category[] = ['all', 'system', 'design', 'business', 'thinking'];

  const filtered = skills.filter((s) => {
    if (category !== 'all') {
      const cat = SKILL_CATEGORIES[s.name];
      if (cat !== category) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const zhName = getSkillDisplayName(s.name);
      return (
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        zhName.toLowerCase().includes(q) ||
        s.author.toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) => {
    const aIsThinking = SKILL_CATEGORIES[a.name] === 'thinking' ? 1 : 0;
    const bIsThinking = SKILL_CATEGORIES[b.name] === 'thinking' ? 1 : 0;
    return aIsThinking - bIsThinking;
  });

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.eyebrow}>skill market</span>
          <h1 className={styles.title}>{uiText("Skill 市场")}</h1>
        </div>
        <div className={styles.headerSpacer} />
        <span className={styles.eyebrow}>{skills.length} {uiText("个可用技能")}</span>
      </div>

      {/* Banner Carousel */}
      <div className={styles.bannerWrap}>
        <div className={styles.bannerTrack} style={{ transform: `translateX(-${bannerIdx * 100}%)` }}>
          {/* ═══ Banner 1: 人格蒸馏 ═══ */}
          <div style={{
            minWidth: '100%', padding: '16px 24px', boxSizing: 'border-box',
            background: 'linear-gradient(115deg, #1a0f2a 0%, #2a1748 55%, #1f1238 100%)',
            color: '#f5ecd5', position: 'relative', overflow: 'hidden',
            border: '1px solid rgba(212,168,74,0.22)',
            display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: '0 12px 40px rgba(20,8,40,0.5)',
          }}>
            {/* starfield */}
            <div style={{position: 'absolute', inset: 0, background: 'radial-gradient(rgba(245,236,213,0.3) 0.5px, transparent 1px) 0 0/28px 28px, radial-gradient(rgba(212,168,74,0.22) 0.3px, transparent 0.7px) 14px 14px/40px 40px', opacity: 0.45, pointerEvents: 'none'}} />
            {/* SVG illustration */}
            <div style={{position: 'relative', flex: '0 0 auto', width: 180, height: 90}}>
              <svg viewBox="0 0 320 150" width="180" height="90">
                <defs>
                  <linearGradient id="b1_liq1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e8c87a" /><stop offset="100%" stopColor="#7a3a8a" /></linearGradient>
                  <radialGradient id="b1_agentGlow1" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="rgba(232,200,122,0.55)" /><stop offset="100%" stopColor="transparent" /></radialGradient>
                  <linearGradient id="b1_portraitFill1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(232,200,122,0.95)" /><stop offset="100%" stopColor="rgba(232,200,122,0.55)" /></linearGradient>
                  <marker id="b1_arrow1" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 0 L 10 5 L 0 10 Z" fill="#e8c87a" /></marker>
                </defs>
                <g>
                  <path d="M 20 130 L 20 70 C 20 38 38 22 60 22 C 78 22 90 30 96 42 C 100 50 100 60 96 68 C 92 76 84 80 76 80 L 76 92 C 76 98 80 102 86 102 L 92 102 L 92 110 C 92 118 86 124 78 124 L 60 124 L 60 130 Z" fill="url(#b1_portraitFill1)" stroke="#d4a84a" strokeWidth="1.2" strokeLinejoin="round" />
                  <circle cx="58" cy="56" r="9" fill="none" stroke="#1a0f2a" strokeWidth="1.4" />
                  <line x1="67" y1="56" x2="74" y2="56" stroke="#1a0f2a" strokeWidth="1.4" />
                  <path d="M 20 130 L 60 130 L 60 120 Q 40 116, 28 126 Z" fill="#d4a84a" opacity="0.6" />
                  <text x="22" y="146" fill="#d4a84a" fontFamily="serif" fontSize="8" letterSpacing="2" opacity="0.7">— MASTER —</text>
                </g>
                {[{cx:130,cy:40,r:4},{cx:142,cy:60,r:3.5},{cx:132,cy:80,r:4},{cx:144,cy:100,r:3},{cx:154,cy:50,r:2.5},{cx:158,cy:90,r:2.5}].map((d,i) => (
                  <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="#e8c87a" opacity={0.85} />
                ))}
                <path d="M 130 40 Q 160 55, 180 80" stroke="#d4a84a" strokeWidth="1" strokeDasharray="2 3" fill="none" opacity="0.7" />
                <path d="M 132 80 Q 156 80, 180 84" stroke="#d4a84a" strokeWidth="1" strokeDasharray="2 3" fill="none" opacity="0.7" />
                <path d="M 144 100 Q 162 96, 180 92" stroke="#d4a84a" strokeWidth="1" strokeDasharray="2 3" fill="none" opacity="0.7" />
                <g>
                  <circle cx="202" cy="100" r="40" fill="url(#b1_agentGlow1)" />
                  <path d="M 194 50 L 194 70 L 180 110 Q 177 122, 190 122 L 214 122 Q 227 122, 224 110 L 210 70 L 210 50 Z" fill="rgba(20,10,36,0.85)" stroke="#d4a84a" strokeWidth="1.4" strokeLinejoin="round" />
                  <path d="M 181 92 L 180 110 Q 177 122, 190 122 L 214 122 Q 227 122, 224 110 L 223 92 Z" fill="url(#b1_liq1)" />
                  <ellipse cx="202" cy="93" rx="20" ry="2" fill="rgba(245,236,213,0.45)" />
                  <circle cx="194" cy="108" r="2" fill="rgba(245,236,213,0.6)" />
                  <circle cx="209" cy="114" r="1.5" fill="rgba(245,236,213,0.5)" />
                  <rect x="192" y="44" width="20" height="8" rx="1.5" fill="#d4a84a" stroke="#a07a2a" strokeWidth="1" />
                </g>
                <path d="M 232 100 L 256 100" stroke="#e8c87a" strokeWidth="1.6" markerEnd="url(#b1_arrow1)" fill="none" />
                <g transform="translate(260, 50)">
                  <rect x="0" y="20" width="50" height="40" rx="10" fill="#7a3a8a" stroke="#e8c87a" strokeWidth="1.5" />
                  <line x1="25" y1="20" x2="25" y2="10" stroke="#e8c87a" strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="25" cy="8" r="3" fill="#e8c87a"><animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" /></circle>
                  <circle cx="14" cy="38" r="4" fill="#f5ecd5" /><circle cx="14" cy="38" r="1.6" fill="#1a0f2a" />
                  <circle cx="36" cy="38" r="4" fill="#f5ecd5" /><circle cx="36" cy="38" r="1.6" fill="#1a0f2a" />
                  <path d="M 18 50 Q 25 54, 32 50" stroke="#f5ecd5" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                  <text x="25" y="76" fill="#e8c87a" fontFamily="serif" fontSize="8" letterSpacing="2" textAnchor="middle">AGENT</text>
                </g>
              </svg>
            </div>
            {/* Title block */}
            <div style={{position: 'relative', flex: '0 0 auto', maxWidth: 300, minWidth: 0}}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '3px 11px',
                background: 'rgba(212,168,74,0.16)', border: '1px solid rgba(212,168,74,0.55)',
                borderRadius: 999, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase' as const,
                color: '#e8c87a', fontWeight: 600,
              }}>
                <span style={{fontSize: 12}}>⚗︎</span> {uiText("人格蒸馏 · 女娲造人")}
              </div>
              <div style={{fontWeight: 600, fontSize: 17, letterSpacing: -0.3, lineHeight: 1.2, marginTop: 8, color: '#f5ecd5'}}>
                {uiText("抽出一位大师的思维，")}<br/>
                <span style={{color: '#e8c87a', fontStyle: 'italic'}}>{uiText("注入")}</span>{uiText("一只 Agent。")}
              </div>
              <div style={{fontSize: 11, color: 'rgba(245,236,213,0.72)', lineHeight: 1.5, marginTop: 4}}>
                {uiText("蒸馏决策框架、第一性原理与表达 DNA — 炼制一位可随时召唤的思维顾问。")}
              </div>
            </div>
            {/* Divider */}
            <div style={{flex: '0 0 auto', width: 1, alignSelf: 'stretch', borderLeft: '1px dashed rgba(212,168,74,0.4)', margin: '4px 0'}} />
            {/* Skill chips */}
            <div style={{position: 'relative', flex: 1, minWidth: 0}}>
              <div style={{display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10}}>
                <span style={{fontSize: 10, color: '#e8c87a', letterSpacing: 3, textTransform: 'uppercase' as const, fontWeight: 600}}>{uiText("本期 SKILL")}</span>
                <span style={{fontSize: 10, color: 'rgba(245,236,213,0.55)', letterSpacing: 0.5}}>{uiText("· 点击召唤")}</span>
              </div>
              <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                {PERSONA_CHIPS.map((c) => (
                  <button key={c.skill} onClick={() => fetchDetail(c.skill)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px', background: 'rgba(245,236,213,0.06)', color: '#f5ecd5',
                    border: '1px solid rgba(245,236,213,0.18)', borderRadius: 999, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 500, whiteSpace: 'nowrap',
                  }}>
                    <span style={{fontSize: 13}}>{c.emoji}</span>{uiText(c.label)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ═══ Banner 2: 设计师包 ═══ */}
          <div style={{
            minWidth: '100%', padding: '16px 24px', boxSizing: 'border-box',
            background: 'linear-gradient(120deg, #0e2820 0%, #163a2c 55%, #1f4a3a 100%)',
            color: '#e8f2dd', position: 'relative', overflow: 'hidden',
            border: '1px solid rgba(160,220,160,0.18)',
            display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: '0 12px 40px rgba(8,28,20,0.5)',
          }}>
            {/* blueprint grid */}
            <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(rgba(180,220,170,0.06) 1px, transparent 1px) 0 0/24px 24px, linear-gradient(90deg, rgba(180,220,170,0.06) 1px, transparent 1px) 0 0/24px 24px', pointerEvents: 'none'}} />
            <div style={{position: 'absolute', left: 30, top: '50%', transform: 'translateY(-50%)', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(255,180,120,0.18), transparent 70%)', pointerEvents: 'none'}} />
            {/* SVG illustration */}
            <div style={{position: 'relative', flex: '0 0 auto', width: 180, height: 90}}>
              <svg viewBox="0 0 320 150" width="180" height="90">
                <defs>
                  <linearGradient id="b2_canvasGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#fff5e8" /><stop offset="100%" stopColor="#f0d8a8" /></linearGradient>
                  <linearGradient id="b2_rulerGrad" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#f0c46e" /><stop offset="100%" stopColor="#c98b3a" /></linearGradient>
                  <linearGradient id="b2_bezierGrad" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#9ddf7e" /><stop offset="100%" stopColor="#ffb56b" /></linearGradient>
                  <radialGradient id="b2_bulb" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="rgba(255,220,140,0.55)" /><stop offset="100%" stopColor="transparent" /></radialGradient>
                </defs>
                <g transform="translate(18, 16)">
                  <rect x="6" y="6" width="120" height="86" rx="6" fill="#2a5040" stroke="#6ab28b" strokeWidth="0.8" />
                  <rect x="0" y="0" width="120" height="86" rx="6" fill="url(#b2_canvasGrad)" stroke="#1a3a2a" strokeWidth="1.5" />
                  <rect x="8" y="8" width="50" height="14" rx="3" fill="#e34a2a" opacity="0.85" />
                  <rect x="62" y="8" width="50" height="14" rx="3" fill="none" stroke="#1a3a2a" strokeWidth="0.8" strokeDasharray="2 2" />
                  <rect x="8" y="28" width="104" height="6" rx="2" fill="#1a3a2a" opacity="0.55" />
                  <rect x="8" y="38" width="80" height="4" rx="2" fill="#1a3a2a" opacity="0.4" />
                  <rect x="8" y="46" width="90" height="4" rx="2" fill="#1a3a2a" opacity="0.4" />
                  <rect x="8" y="54" width="60" height="4" rx="2" fill="#1a3a2a" opacity="0.4" />
                  <circle cx="14" cy="70" r="5" fill="#e34a2a" stroke="#1a3a2a" strokeWidth="0.8" />
                  <circle cx="26" cy="70" r="5" fill="#1a3a2a" stroke="#1a3a2a" strokeWidth="0.8" />
                  <circle cx="38" cy="70" r="5" fill="#f0c46e" stroke="#1a3a2a" strokeWidth="0.8" />
                  <circle cx="50" cy="70" r="5" fill="#6ab28b" stroke="#1a3a2a" strokeWidth="0.8" />
                  <rect x="60" y="65" width="50" height="10" rx="2" fill="#6ab28b" opacity="0.4" stroke="#1a3a2a" strokeWidth="0.6" />
                  <text x="0" y="-4" fill="#9ddf7e" fontFamily="monospace" fontSize="8" letterSpacing="1.5">artboard · 01</text>
                </g>
                <g transform="translate(20, 110)">
                  <rect x="0" y="0" width="180" height="14" rx="2" fill="url(#b2_rulerGrad)" stroke="#7d5a20" strokeWidth="0.8" />
                  {Array.from({length: 18}).map((_, i) => (
                    <line key={i} x1={i*10+4} y1="0" x2={i*10+4} y2={i%5===0?10:6} stroke="#3a2a10" strokeWidth="0.8" />
                  ))}
                </g>
                <g transform="translate(170, 16)">
                  <path d="M 0 80 L 30 0 L 90 90 L 130 10" stroke="#9ddf7e" strokeWidth="0.7" strokeDasharray="2 3" opacity="0.55" fill="none" />
                  <path d="M 0 80 C 30 0, 90 90, 130 10" stroke="url(#b2_bezierGrad)" strokeWidth="2.4" fill="none" strokeLinecap="round" />
                  <circle cx="0" cy="80" r="3.6" fill="#9ddf7e" stroke="#0e2820" strokeWidth="1.2" />
                  <circle cx="130" cy="10" r="3.6" fill="#9ddf7e" stroke="#0e2820" strokeWidth="1.2" />
                  <circle cx="30" cy="0" r="2.4" fill="#0e2820" stroke="#9ddf7e" strokeWidth="1" />
                  <circle cx="90" cy="90" r="2.4" fill="#0e2820" stroke="#9ddf7e" strokeWidth="1" />
                  <circle cx="138" cy="14" r="6" fill="url(#b2_bulb)" />
                  <text x="0" y="-4" fill="#9ddf7e" fontFamily="monospace" fontSize="8" letterSpacing="1.5">bezier · taste DNA</text>
                </g>
                <g transform="translate(212, 110)">
                  <rect x="0" y="0" width="28" height="20" rx="3" fill="rgba(255,255,255,0.06)" stroke="#9ddf7e" strokeWidth="0.8" />
                  <text x="6" y="15" fill="#9ddf7e" fontFamily="serif" fontSize="14" fontWeight="700">Aa</text>
                </g>
                <g transform="translate(248, 110)">
                  <rect x="0" y="0" width="28" height="20" rx="3" fill="rgba(255,255,255,0.06)" stroke="#9ddf7e" strokeWidth="0.8" />
                  <path d="M 6 12 L 14 4 L 20 10 L 12 18 Z" fill="#ffb56b" stroke="#0e2820" strokeWidth="0.8" />
                  <line x1="12" y1="18" x2="10" y2="20" stroke="#0e2820" strokeWidth="1" strokeLinecap="round" />
                </g>
              </svg>
            </div>
            {/* Title block */}
            <div style={{position: 'relative', flex: '0 0 auto', maxWidth: 300, minWidth: 0}}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '3px 11px',
                background: 'rgba(157,223,126,0.14)', border: '1px solid rgba(157,223,126,0.55)',
                borderRadius: 999, fontFamily: 'monospace', fontSize: 10,
                letterSpacing: 2, textTransform: 'uppercase' as const, color: '#9ddf7e', fontWeight: 600,
              }}>
                <span style={{fontSize: 12}}>◆</span> {uiText("设计师包 · DESIGN")}
              </div>
              <div style={{fontWeight: 800, fontSize: 17, letterSpacing: -0.5, lineHeight: 1.2, marginTop: 8, color: '#f0f8e6'}}>
                {uiText("把世界级设计标准")}<br/>
                <span style={{background: 'linear-gradient(90deg, #9ddf7e, #ffb56b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{uiText("蒸馏")}</span> {uiText("进 Agent 的肌肉记忆。")}
              </div>
              <div style={{fontSize: 11, color: 'rgba(232,242,221,0.7)', lineHeight: 1.5, marginTop: 4}}>
                {uiText("页面设计、演示文稿、AI 图像生成 — 让你的 Agent 拥有专业设计师水准。")}
              </div>
            </div>
            {/* Divider */}
            <div style={{flex: '0 0 auto', width: 1, alignSelf: 'stretch', borderLeft: '1px dashed rgba(157,223,126,0.35)', margin: '4px 0'}} />
            {/* Skill chips */}
            <div style={{position: 'relative', flex: 1, minWidth: 0}}>
              <div style={{display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10}}>
                <span style={{fontFamily: 'monospace', fontSize: 10, color: '#9ddf7e', letterSpacing: 2, textTransform: 'uppercase' as const, fontWeight: 600}}>{uiText("本期 SKILL")}</span>
                <span style={{fontSize: 10, color: 'rgba(232,242,221,0.55)', letterSpacing: 0.5}}>{uiText("· 点击启用")}</span>
              </div>
              <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                {DESIGN_CHIPS.map((c) => (
                  <button key={c.skill} onClick={() => fetchDetail(c.skill)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px', background: 'rgba(232,242,221,0.05)', color: '#e8f2dd',
                    border: '1px solid rgba(157,223,126,0.25)', borderRadius: 8, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 500, whiteSpace: 'nowrap',
                  }}>
                    <span style={{fontSize: 13}}>{c.emoji}</span>{uiText(c.label)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Shared dot indicators */}
        <div style={{position: 'absolute', right: 20, bottom: 12, display: 'flex', gap: 5}}>
          <span onClick={() => goToSlide(0)} style={{width: bannerIdx === 0 ? 18 : 6, height: 4, borderRadius: 2, background: bannerIdx === 0 ? '#d4a84a' : 'rgba(200,200,200,0.3)', cursor: 'pointer'}} />
          <span onClick={() => goToSlide(1)} style={{width: bannerIdx === 1 ? 18 : 6, height: 4, borderRadius: 2, background: bannerIdx === 1 ? '#9ddf7e' : 'rgba(200,200,200,0.3)', cursor: 'pointer'}} />
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={uiText("搜索技能名称、描述或标签...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
        />
        <button className={styles.searchBtn}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>

      {/* Category tabs */}
      <div className={styles.categoryBar}>
        {categories.map((cat) => (
          <button
            key={cat}
            className={`${styles.categoryChip} ${category === cat ? styles.categoryActive : ''}`}
            onClick={() => setCategory(cat)}
          >
            {getCategoryLabel(cat)}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className={styles.empty}>
          <div className={styles.emptyHint}>{uiText("加载中...")}</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>🔍</div>
          <div className={styles.emptyTitle}>{uiText("没有找到匹配的技能")}</div>
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((skill) => (
            <div
              key={skill.id}
              className={styles.card}
              onClick={() => fetchDetail(skill.name)}
            >
              {/* Cover area */}
              <div
                className={styles.cardCover}
                style={getSkillCover(skill.name)
                  ? {
                      backgroundImage: `url(${getSkillCover(skill.name)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center 15%',
                      backgroundRepeat: 'no-repeat',
                    }
                  : { background: getSkillAccentColor(skill.name) }
                }
              >
                {!getSkillCover(skill.name) && (
                  <span className={styles.cardCoverIcon}>{getSkillIcon(skill.name)}</span>
                )}
                {getSkillCategoryLabel(skill.name) && (
                  <span className={styles.cardCategoryLabel}>{getSkillCategoryLabel(skill.name)}</span>
                )}
              </div>
              {/* Text area */}
              <div className={styles.cardBody}>
                <div className={styles.cardName}>
                  {getSkillDisplayName(skill.name)}
                </div>
                <div className={styles.cardDesc}>
                  {skill.description.length > 100
                    ? skill.description.slice(0, 100) + '...'
                    : skill.description || uiText("无描述")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedSkill && (
        <div className={styles.modalOverlay} onClick={closeDetail}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <span className={styles.detailIcon}>{getSkillIcon(selectedSkill.name)}</span>
                {getSkillDisplayName(selectedSkill.name)}
              </h2>
              <div className={styles.modalActions}>
                {bots.length > 0 && !installTarget && (
                  <button
                    type="button"
                    className={styles.installBtnLarge}
                    onClick={() => setInstallTarget(selectedSkill.name)}
                  >
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 4}}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {t('skill.install')}
                  </button>
                )}
                <button type="button" className={styles.modalClose} onClick={closeDetail}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.modalMeta}>
              <span className={styles.detailAuthor}>{selectedSkill.author}</span>
              {selectedSkill.tags?.length > 0 &&
                selectedSkill.tags.map((tg) => (
                  <span key={tg} className={styles.tag}>{tg}</span>
                ))
              }
            </div>

            <div className={styles.modalBody}>
              {installTarget ? (
                <div className={styles.agentSelector}>
                  <div className={styles.agentSelectorTitle}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    {uiText("选择要安装到的 Agent")}
                  </div>
                  <div className={styles.agentSelectorHint}>
                    {uiText("将")} <strong>{getSkillDisplayName(selectedSkill.name)}</strong> {uiText("安装给以下 Agent：")}
                  </div>
                  <div className={styles.agentSelectorList}>
                    {bots.map((b) => {
                      const av = getAvatar(b.name);
                      const isInstalling = installing === b.name;
                      const isSuccess = installSuccess === b.name;
                      return (
                        <button
                          key={b.name}
                          className={`${styles.agentSelectorCard} ${isSuccess ? styles.agentSelectorCardSuccess : ''}`}
                          onClick={() => !isInstalling && !isSuccess && handleInstall(selectedSkill.name, b.name)}
                          disabled={isInstalling}
                        >
                          <Avatar name={b.name} size="sm" imageUrl={av.imageUrl} presetColor={av.presetColor} />
                          <div className={styles.agentSelectorInfo}>
                            <div className={styles.agentSelectorName}>{b.name}</div>
                            {b.description && (
                              <div className={styles.agentSelectorDesc}>
                                {b.description.length > 50 ? b.description.slice(0, 50) + '...' : b.description}
                              </div>
                            )}
                          </div>
                          <span className={styles.agentSelectorAction}>
                            {isSuccess ? (
                              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#5a6a2a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : isInstalling ? (
                              <span className={styles.agentSelectorSpinner} />
                            ) : (
                              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className={styles.agentSelectorBack}
                    onClick={() => setInstallTarget(null)}
                  >
                    {uiText("← 返回详情")}
                  </button>
                </div>
              ) : (
                <>
                  {selectedSkill.description && (
                    <p className={styles.detailDesc}>{selectedSkill.description}</p>
                  )}
                  {detailLoading ? (
                    <div className={styles.emptyHint}>{uiText("加载中...")}</div>
                  ) : (
                    <div className={styles.markdownContent}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                        {stripFrontmatter(selectedSkill.skillMd)}
                      </ReactMarkdown>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
